/**
 * What KIND of place a booking sends the customer to, in words.
 *
 * `tenant_locations.location_type` already distinguishes a lakeside meeting
 * point (`ride_lesson`) from a shop you collect gear at (`storefront`). The
 * booking UI never said which, and the two surfaces that named it hardcoded
 * OPPOSITE words for the same place: the detail row said "Meeting point" while
 * the panel below it was headed "Pickup location". One of them was always
 * wrong, and which one depended on the product.
 *
 * That matters most where one dealer runs both in the same area. Adventure
 * Elevated's Lake Powell has a lesson launch at State Line Boat Ramp and a gear
 * pickup at Green Adventure Sports, 15 miles apart — a customer who reads
 * "pickup" and drives to the shop for a lesson has lost their booking.
 *
 * Derived, never configured: the dealer already answered this when they set the
 * location type, so asking again in the section editor would be a knob for a
 * question with a known answer.
 */
export type LocationKind = 'meet' | 'pickup' | 'unknown'

export function locationKindFrom(locationType: string | null | undefined): LocationKind {
  if (locationType === 'ride_lesson') return 'meet'
  if (locationType === 'storefront') return 'pickup'
  // 'mailing' never reaches a booking surface (the pickup query excludes it),
  // and an unrecognised type must not assert the wrong verb — fall back to
  // neutral wording rather than guessing.
  return 'unknown'
}

export type LocationKindCopy = {
  /** Label for the compact detail row beside the map pin. */
  detailLabel: string
  /** Heading over the map panel on /bookings/[slug]. */
  heading: string
  /** One line telling the customer what actually happens there. Null when the
   *  kind is unknown — silence beats a confident wrong sentence. */
  note: string | null
  /** Inline lead-in for the single-line embed ("Meet at Deer Creek…"). */
  inlineLabel: string
}

export function locationKindCopy(kind: LocationKind): LocationKindCopy {
  if (kind === 'meet') {
    return {
      detailLabel: 'Meeting point',
      heading: 'Where you’ll meet',
      note: 'Meet your guide at the water — there’s nothing to pick up first.',
      inlineLabel: 'Meet at',
    }
  }
  if (kind === 'pickup') {
    return {
      detailLabel: 'Gear pickup',
      heading: 'Where to pick up your gear',
      note: 'Collect your gear at the shop, then head to the water.',
      inlineLabel: 'Pick up at',
    }
  }
  return {
    detailLabel: 'Location',
    heading: 'Location',
    note: null,
    inlineLabel: 'At',
  }
}
